import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import useAsyncModifyListView from '../../../../hooks/useAsyncModifyListView';
import { useGetViews, useUpdateView } from '../../../../sic-lib/api/hooks/useViews';
import { DEFAULT_LIST_ID, DEFAULT_VIEW_ID } from '../../../../sic-lib/defaults';
import { RequestStatus } from '../../../../sic-lib/enums/RequestStatus';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';
import { ListItem } from '../../../../sic-lib/types/Lists';
import { ViewStyle } from '../../../../sic-lib/types/View';
import { ViewProperty } from '../../../../sic-lib/types/ViewProperty';
import { buildUpdateListRequest } from '../../../../sic-lib/utils/contactListHelpers';
import { hasMatchingViewId } from '../../../../sic-lib/utils/functionalFilters';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { updateView } from '../../../../store/views';
import SelectViewProperties from '../../../Settings/ClientViews/SelectViewProperties/SelectViewProperties';
import ButtonLoading from '../../../Shared/ButtonLoading';
import { DrawerCommon } from '../../../Shared/Drawer/DrawerCommon';
import DraggableViewProperties from '../../ContactGroups/DraggableViewProperties/DraggableViewProperties';
import SelectView from '../SelectView/SelectView';

const useStyles = makeStyles()((theme: Theme) => ({
    btnCancel: {
        color: theme.palette.btnDefault.main,
        '&:hover': {
            backgroundColor: theme.palette.btnDefault.light,
        },
    },
}));

interface ComponentState {
    viewId: number;
    viewName: string;
    viewStyle: ViewStyle;
    viewProperties: ViewProperty[];
}

interface Props {
    drawerId: string;
    isOpen: boolean;
    list: ListItem;
    onCloseHandler: () => void;
}

export default function DrawerListViewOptionsRaw({
    drawerId,
    isOpen,
    list,
    onCloseHandler,
}: Props) {
    const dispatch = useAppDispatch();
    const { classes } = useStyles();

    const { requestStatus, asyncModifyListView } = useAsyncModifyListView();

    const appliedFilters = useAppSelector(state => state.conversationGrid.filters);
    const defaultListView = useAppSelector(state => state.view.view);

    const { data: viewsData } = useGetViews();

    const views = React.useMemo(() => {
        return viewsData || [];
    }, [viewsData]);

    const { mutate: updateViewMutation, isPending: isViewMutationLoading } = useUpdateView();

    const [compState, compDispatch] = React.useReducer<GenericReducer<ComponentState>>(
        genericReducer,
        {
            viewId: list.viewId,
            viewName: '',
            viewStyle: ViewStyle.list,
            viewProperties: [],
        },
    );

    React.useEffect(() => {
        const matchedView =
            list.listId === -1 ? defaultListView : views.filter(hasMatchingViewId(list.viewId))[0];

        if (matchedView) {
            compDispatch({
                viewId: matchedView.viewId,
                viewName: matchedView.name,
                viewStyle: matchedView.style,
                viewProperties: matchedView.properties,
            });
        }
    }, [list.viewId, views]);

    const handleSubmit = async () => {
        const matchedView = views.filter(hasMatchingViewId(compState.viewId))[0];

        if (!matchedView) return;
        const nextList = buildUpdateListRequest({
            ...list,
            ...{
                viewId: compState.viewId,
                filters: appliedFilters,
            },
        });

        const nextView = {
            ...matchedView,
            ...{
                name: compState.viewName,
                style: compState.viewStyle,
                properties: compState.viewProperties,
            },
        };

        const asyncResult = await asyncModifyListView(nextList);

        if (asyncResult) {
            updateViewMutation(nextView);
        }

        onCloseHandler();
    };

    const handleApply = () => {
        const matchedView = views.filter(hasMatchingViewId(compState.viewId))[0];

        if (matchedView) {
            dispatch(
                updateView({
                    viewData: {
                        ...matchedView,
                        properties: compState.viewProperties,
                    },
                }),
            );

            onCloseHandler();
        }
    };

    return (
        <DrawerCommon
            open={isOpen}
            anchor="right"
            handleOnClose={onCloseHandler}
            title="Layout Options"
            description="Layouts are assigned to lists to determine how contacts are viewed and what values are displayed"
            footer={
                <Stack direction="row" spacing={2} p={2}>
                    {list.listId === DEFAULT_LIST_ID ? (
                        <Button color="secondary" variant="outlined" onClick={handleApply}>
                            Apply
                        </Button>
                    ) : (
                        <ButtonLoading
                            isLoading={
                                requestStatus === RequestStatus.active || isViewMutationLoading
                            }
                            onClick={handleSubmit}
                            color="secondary"
                            variant="outlined"
                        >
                            Save
                        </ButtonLoading>
                    )}
                    <Button className={classes.btnCancel} onClick={onCloseHandler}>
                        Cancel
                    </Button>
                </Stack>
            }
        >
            <Grid container direction="column" wrap="nowrap" className="listViews">
                <Grid item xs="auto" className="listViews__section">
                    <SelectView
                        id="select_view"
                        label="Select View"
                        name="select_view"
                        variant="outlined"
                        size="small"
                        value={compState.viewId}
                        onSelectHandler={compDispatch}
                    />
                </Grid>
                <Grid item xs={true} className="listViews__wrapper--columns">
                    <Grid container direction="column" wrap="nowrap">
                        <Grid item xs="auto" className="listViews__section--scroll">
                            <DraggableViewProperties
                                viewProperties={compState.viewProperties}
                                onChangeHandler={compDispatch}
                            />
                        </Grid>
                        <Grid item xs="auto" className="listViews__section">
                            {compState.viewId === DEFAULT_VIEW_ID ? (
                                list.listId === DEFAULT_LIST_ID && (
                                    <SelectViewProperties
                                        label="Add Property"
                                        variant="outlined"
                                        size="small"
                                        SelectProps={{
                                            MenuProps: { disablePortal: true },
                                        }}
                                        viewProperties={compState.viewProperties}
                                        onSelectHandler={compDispatch}
                                    />
                                )
                            ) : (
                                <SelectViewProperties
                                    label="Add Property"
                                    variant="outlined"
                                    size="small"
                                    SelectProps={{
                                        MenuProps: { disablePortal: true },
                                    }}
                                    viewProperties={compState.viewProperties}
                                    onSelectHandler={compDispatch}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </DrawerCommon>
    );
}
